import React, { useEffect, useState } from "react";
import { UserPlus, Shield, Loader2, Trash2, KeyRound, History, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const ROLES = [
    { v: "platform_admin", label: "Platform admin", detail: "Full access — billing, users, content, everything." },
    { v: "recruiter_admin", label: "Recruiter admin", detail: "Talent, briefs, verifications, pipelines. No billing." },
    { v: "support_admin", label: "Support admin", detail: "Support tickets, account recovery. No moderation." },
];

const roleTone = (r) => ({
    platform_admin: "bg-violet-500/15 text-violet-700 dark:text-violet-400 border-violet-500/30",
    recruiter_admin: "bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/30",
    support_admin: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
}[r] || "bg-slate-500/15 text-slate-600 dark:text-slate-400 border-slate-500/30");

export default function AdminTeam({ currentAdminEmail }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    // Activity feed (audit_log ∩ action=admin.*). Loaded alongside the roster
    // so both refresh together after any team mutation.
    const [activity, setActivity] = useState([]);
    const [activityLoading, setActivityLoading] = useState(true);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [form, setForm] = useState({ email: "", password: "", admin_role: "recruiter_admin" });
    const [submitting, setSubmitting] = useState(false);
    // Force-reset flow — a platform_admin sets a new password for a peer.
    // Kept separate from the invite dialog so the two intents can't be
    // confused (invite = new row, reset = existing row).
    const [resetTarget, setResetTarget] = useState(null);   // email string
    const [resetPassword, setResetPassword] = useState("");
    const [resetSubmitting, setResetSubmitting] = useState(false);

    const load = async () => {
        try {
            setLoading(true);
            setActivityLoading(true);
            const [roster, feed] = await Promise.all([
                api.get("/admin/team"),
                api.get("/admin/team/activity?limit=30"),
            ]);
            setRows(roster.data);
            setActivity(feed.data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
            setActivityLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const invite = async () => {
        try {
            setSubmitting(true);
            await api.post("/admin/team", form);
            toast.success("Admin invited.");
            setInviteOpen(false);
            setForm({ email: "", password: "", admin_role: "recruiter_admin" });
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    const changeRole = async (email, role) => {
        try {
            await api.patch(`/admin/team/${encodeURIComponent(email)}`, { admin_role: role });
            toast.success("Role updated.");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const remove = async (email) => {
        if (!window.confirm(`Remove admin ${email}?`)) return;
        try {
            await api.delete(`/admin/team/${encodeURIComponent(email)}`);
            toast.success("Removed.");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const doPeerReset = async () => {
        if (!resetTarget) return;
        try {
            setResetSubmitting(true);
            await api.post(`/admin/team/${encodeURIComponent(resetTarget)}/reset-password`, {
                new_password: resetPassword,
            });
            toast.success(`Password reset. ${resetTarget} has been notified by email.`);
            setResetTarget(null);
            setResetPassword("");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setResetSubmitting(false);
        }
    };

    return (
        <div data-testid="admin-team-page">
            <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
                <div>
                    <p className="overline">Platform</p>
                    <h1 data-testid="dash-page-title" className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">
                        Admin Team & Permissions
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">Manage who can access what inside Simonara Ops.</p>
                </div>
                <button onClick={() => setInviteOpen(true)} className="btn-green text-sm inline-flex items-center gap-1" data-testid="team-invite">
                    <UserPlus size={13} /> Invite admin
                </button>
            </div>

            <div className="card-soft p-4 mb-6">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">Role reference</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {ROLES.map((r) => (
                        <div key={r.v} className="rounded-xl bg-muted/40 border border-border p-3">
                            <Badge className={`text-[10px] border ${roleTone(r.v)}`}>{r.label}</Badge>
                            <p className="mt-1 text-xs text-muted-foreground">{r.detail}</p>
                        </div>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className="py-16 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>
            ) : (
                <div className="card-soft overflow-hidden">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-border text-left text-[11px] uppercase text-muted-foreground">
                                <th className="p-3 font-semibold">Email</th>
                                <th className="p-3 font-semibold">Role</th>
                                <th className="p-3 font-semibold">Invited by</th>
                                <th className="p-3 font-semibold">Added</th>
                                <th className="p-3 font-semibold text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((a) => {
                                const isMe = a.email === currentAdminEmail;
                                return (
                                    <tr key={a.email} className="border-b border-border/60 last:border-0" data-testid={`team-row-${a.email}`}>
                                        <td className="p-3 text-sm text-foreground">{a.email}{isMe && <span className="ml-2 text-[10px] text-muted-foreground">(you)</span>}</td>
                                        <td className="p-3">
                                            <Select value={a.admin_role || "platform_admin"} onValueChange={(v) => changeRole(a.email, v)}>
                                                <SelectTrigger className="h-8 w-40 text-xs" data-testid={`team-role-${a.email}`}><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    {ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        </td>
                                        <td className="p-3 text-xs text-muted-foreground whitespace-nowrap" data-testid={`team-invited-by-${a.email}`}>
                                            {a.invited_by ? (
                                                <div className="leading-tight">
                                                    <div className="text-foreground/80">{a.invited_by}</div>
                                                    {a.invited_at && (
                                                        <div className="text-[10px] text-muted-foreground">
                                                            {new Date(a.invited_at).toLocaleDateString()}
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="italic text-muted-foreground/70" title="Seeded on backend boot (no invite record)">
                                                    seed / bootstrap
                                                </span>
                                            )}
                                        </td>
                                        <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">{a.created_at ? new Date(a.created_at).toLocaleDateString() : "—"}</td>
                                        <td className="p-3 text-right">
                                            <div className="inline-flex items-center gap-1">
                                                <button
                                                    onClick={() => { setResetTarget(a.email); setResetPassword(""); }}
                                                    disabled={isMe}
                                                    className="text-[11px] px-2 py-1 rounded border border-border text-foreground/80 hover:bg-muted/50 disabled:opacity-40 inline-flex items-center gap-1"
                                                    data-testid={`team-reset-${a.email}`}
                                                    title={isMe ? "Use 'Change my password' in the sidebar" : `Force-reset ${a.email}'s password`}
                                                >
                                                    <KeyRound size={11} /> Reset password
                                                </button>
                                                <button
                                                    onClick={() => remove(a.email)}
                                                    disabled={isMe}
                                                    className="text-[11px] px-2 py-1 rounded border border-destructive/30 text-destructive hover:bg-destructive/5 disabled:opacity-40 inline-flex items-center gap-1"
                                                    data-testid={`team-remove-${a.email}`}
                                                >
                                                    <Trash2 size={11} /> Remove
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Activity timeline — all admin.* audit_log events, newest first. */}
            <ActivityFeed rows={activity} loading={activityLoading} />

            <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
                <DialogContent data-testid="team-invite-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><Shield size={16} className="text-emerald-500" /> Invite an admin</DialogTitle>
                        <DialogDescription>Send someone credentials to log into Simonara Ops with a specific role.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <Input placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="team-form-email" />
                        <Input type="password" placeholder="Temporary password (min 8 chars)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="team-form-password" />
                        <Select value={form.admin_role} onValueChange={(v) => setForm({ ...form, admin_role: v })}>
                            <SelectTrigger data-testid="team-form-role"><SelectValue /></SelectTrigger>
                            <SelectContent>{ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <DialogFooter>
                        <button onClick={() => setInviteOpen(false)} className="btn-outline text-sm px-4 py-2">Cancel</button>
                        <button
                            onClick={invite}
                            disabled={submitting || !form.email.trim() || form.password.length < 8}
                            className="btn-green text-sm px-4 py-2 disabled:opacity-60"
                            data-testid="team-form-submit"
                        >
                            {submitting ? <Loader2 size={13} className="animate-spin" /> : "Invite"}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Peer force-reset dialog. platform_admin only per backend. */}
            <Dialog open={!!resetTarget} onOpenChange={(v) => { if (!v) { setResetTarget(null); setResetPassword(""); } }}>
                <DialogContent data-testid="team-reset-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <KeyRound size={16} className="text-emerald-500" /> Reset admin password
                        </DialogTitle>
                        <DialogDescription>
                            Set a new password for <b>{resetTarget}</b>. They&apos;ll get an email
                            notice showing that you did this. Share the new password with them
                            through a secure channel (Signal, 1Password, etc.) — not email.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <Input
                            type="password"
                            placeholder="New password (min 8 chars)"
                            value={resetPassword}
                            onChange={(e) => setResetPassword(e.target.value)}
                            data-testid="team-reset-password-input"
                            autoFocus
                        />
                    </div>
                    <DialogFooter>
                        <button
                            onClick={() => { setResetTarget(null); setResetPassword(""); }}
                            className="btn-outline text-sm px-4 py-2"
                            data-testid="team-reset-cancel"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={doPeerReset}
                            disabled={resetSubmitting || resetPassword.length < 8}
                            className="btn-green text-sm px-4 py-2 disabled:opacity-60"
                            data-testid="team-reset-submit"
                        >
                            {resetSubmitting ? <Loader2 size={13} className="animate-spin" /> : "Reset password"}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Activity feed — renders the last N admin.* audit_log rows as a compact
// timeline. Only shown to platform_admin (backend gates the endpoint).
// ---------------------------------------------------------------------------
const ACTION_META = {
    "admin.invite":                { verb: "invited",             tone: "emerald" },
    "admin.role_change":           { verb: "changed role of",     tone: "sky"     },
    "admin.remove":                { verb: "removed",             tone: "rose"    },
    "admin.reset":                 { verb: "force-reset password of", tone: "amber"   },
    "admin.password_change_self":  { verb: "rotated own password", tone: "slate"   },
};

const toneClass = (tone) => ({
    emerald: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
    sky:     "bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/30",
    rose:    "bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/30",
    amber:   "bg-amber-500/15 text-amber-800 dark:text-amber-400 border-amber-500/30",
    slate:   "bg-slate-500/15 text-slate-700 dark:text-slate-400 border-slate-500/30",
}[tone] || "bg-slate-500/15 text-slate-700 dark:text-slate-400 border-slate-500/30");

function _fmtTimeAgo(iso) {
    if (!iso) return "";
    const then = new Date(iso).getTime();
    const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
    if (s < 60)    return `${s}s ago`;
    if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
}

function ActivityFeed({ rows, loading }) {
    return (
        <section className="mt-8" data-testid="team-activity-feed">
            <div className="flex items-center gap-2 mb-3">
                <History size={14} className="text-muted-foreground" />
                <h2 className="font-heading font-semibold text-sm text-foreground">Recent admin activity</h2>
                <span className="text-[11px] text-muted-foreground">· newest first, last 30</span>
            </div>
            <div className="card-soft overflow-hidden">
                {loading ? (
                    <div className="py-8 flex justify-center">
                        <Loader2 size={16} className="animate-spin text-emerald-500" />
                    </div>
                ) : rows.length === 0 ? (
                    <p className="p-4 text-xs text-muted-foreground italic">
                        No admin activity yet. Invites, role changes, resets, and removals will show up here.
                    </p>
                ) : (
                    <ul className="divide-y divide-border">
                        {rows.map((r) => {
                            const meta = ACTION_META[r.action] || { verb: r.action, tone: "slate" };
                            const actor = r.actor || "system";
                            const target = r.target || "—";
                            const isSelf = r.action === "admin.password_change_self";
                            const roleTag = r.meta?.admin_role
                                ? <span className="ml-1 text-[10px] text-muted-foreground">→ {String(r.meta.admin_role).replace(/_/g, " ")}</span>
                                : null;
                            return (
                                <li
                                    key={r.id}
                                    data-testid={`activity-row-${r.id}`}
                                    className="p-3 flex items-center gap-3 hover:bg-muted/30 transition-colors"
                                >
                                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${toneClass(meta.tone)}`}>
                                        {r.action.replace("admin.", "")}
                                    </span>
                                    <div className="flex-1 min-w-0 text-xs text-foreground/90">
                                        <span className="font-medium">{actor}</span>{" "}
                                        <span className="text-muted-foreground">{meta.verb}</span>{" "}
                                        {!isSelf && (
                                            <>
                                                <ArrowRight size={10} className="inline mx-1 text-muted-foreground/60" />
                                                <span className="font-medium">{target}</span>
                                            </>
                                        )}
                                        {roleTag}
                                    </div>
                                    <span
                                        className="text-[10px] text-muted-foreground whitespace-nowrap"
                                        title={r.at || ""}
                                    >
                                        {_fmtTimeAgo(r.at)}
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </section>
    );
}

